import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    ScenarioRunCheck,
    ScenarioRunDocument,
    ScenarioRunEvent,
    ScenarioRunScenario,
} from '../../../api/api';
import { checkKind, formatDuration, Phase, phaseSaysSomething, scenarioPhases } from './runDetailModel';

/** The three lanes of the dossier's flow, in the same order: Chargebee left, Exact right, the connector between. */
export type Lane = 'chargebee' | 'connector' | 'exact';

export type Tone = 'neutral' | 'waiting' | 'ok' | 'warn' | 'error';

/**
 * Where a card stands on a document's way: the flow draws a line from each card of a document to its next one, so the
 * eye follows the document from the step that made it, through the connector apps, to where it ends up.
 */
export interface FlowLink {
    document: string;
    /** Still to come: the line to it is dashed. */
    expected: boolean;
}

/**
 * Where a part of a document's way stands: still to come, at work, held back, through, failing, or - for where the
 * document has to end up - there.
 */
export type WayState = 'expected' | 'busy' | 'waiting' | 'done' | 'retrying' | 'failed' | 'bypassed' | 'there' | 'none';

/** One line of a scenario's flow, in the lane of the system it happened in. */
export type FlowRow =
    | { kind: 'phase'; at: string; phase: Phase }
    | {
          kind: 'step';
          at: string;
          lane: Lane;
          icon: IconProp;
          title: string;
          notes: string[];
          app?: string;
          links: FlowLink[];
          /** The suite's step kind, such as <c>createCustomer</c>. */
          step?: string;
          /** Whether the scenario is doing this step right now: its last step, with no phase begun after it. */
          going?: boolean;
      }
    | {
          kind: 'way';
          /** When it got there; empty while it is still to come. */
          at: string;
          lane: Lane;
          /** A connector app on the way, or where the document has to end up. */
          part: 'app' | 'target';
          app?: string;
          name: string;
          id: string;
          amount?: string;
          state: WayState;
          /** What it does, as a short label: "Booking in Exact", "Booked". */
          text: string;
          /** How long the app took with the document, once it is through. */
          tookMs?: number;
          /** Since when it is at work or holding the document, for a counter while the run goes. */
          sinceUtc?: string;
          detail?: string;
          /** What the runner said in full, when {@link detail} is a short form of it. */
          fullDetail?: string;
          link: FlowLink;
      }
    | { kind: 'note'; at: string; lane: Lane; text: string }
    | { kind: 'problem'; at: string; level: 'warning' | 'error'; text: string }
    | { kind: 'checks'; at: string; passed: number; total: number; failed: string[] };

// Where a suite step acts, and how it looks there. A step the runner does on its own, such as waiting, is the
// connector's lane: that is what it waits on.
const STEP_LOOK: Record<string, { lane: Lane; icon: IconProp; app?: string }> = {
    createCustomer: { lane: 'chargebee', icon: 'user' },
    createItemPrice: { lane: 'chargebee', icon: 'receipt' },
    createSubscription: { lane: 'chargebee', icon: 'layer-group' },
    awaitInvoice: { lane: 'chargebee', icon: 'file-invoice' },
    createCharge: { lane: 'chargebee', icon: 'file-invoice' },
    createCoupon: { lane: 'chargebee', icon: 'receipt' },
    recordPayment: { lane: 'chargebee', icon: 'hand-holding-dollar' },
    payInvoicesTogether: { lane: 'chargebee', icon: 'hand-holding-dollar' },
    createCreditNote: { lane: 'chargebee', icon: 'file-invoice' },
    cancelSubscription: { lane: 'chargebee', icon: 'ban' },
    deliverWebhook: { lane: 'chargebee', icon: 'paper-plane' },
    waitForDrain: { lane: 'connector', icon: 'hourglass-half' },
    wait: { lane: 'connector', icon: 'hourglass-half' },
    payByHandInExact: { lane: 'exact', icon: 'building-columns' },
    runReceivablesSync: { lane: 'connector', icon: 'rotate', app: 'exrcv' },
    awaitChargebeePayment: { lane: 'chargebee', icon: 'hourglass-half' },
    offerAgain: { lane: 'connector', icon: 'rotate-left' },
    scanIssues: { lane: 'connector', icon: 'magnifying-glass' },
};

/** Which way a document travels: an invoice from Chargebee into Exact, a payment made in Exact back to Chargebee. */
export type Direction = 'to-exact' | 'to-chargebee';

/** Where a document stands, as the runner writes it, and how the strip above the flow draws that. */
export const STAGE_LOOK: Record<
    string,
    { lane: Lane; icon: IconProp; tone: Tone; title: string; station: number; hint: string; direction?: Direction }
> = {
    'paid-in-exact': {
        lane: 'exact',
        icon: 'building-columns',
        tone: 'neutral',
        title: 'paid in Exact',
        station: 0,
        hint: 'Booked in Exact. Exact still has to send the webhook for this bank entry.',
        direction: 'to-chargebee',
    },
    'exact-webhook': {
        lane: 'connector',
        icon: 'inbox',
        tone: 'waiting',
        title: "Exact's webhook received",
        station: 1,
        hint: "exrcv has Exact's webhook for the bank entry and is processing it.",
        direction: 'to-chargebee',
    },
    'to-chargebee': {
        lane: 'connector',
        icon: 'robot',
        tone: 'neutral',
        title: 'queued for cbsnd',
        station: 2,
        hint: "exrcv queued the payment, from Exact's webhook or the receivables sync. cbsnd still has to record it in Chargebee.",
        direction: 'to-chargebee',
    },
    'in-chargebee': {
        lane: 'chargebee',
        icon: 'circle-check',
        tone: 'ok',
        title: 'recorded in Chargebee',
        station: 3,
        hint: 'Chargebee has the payment.',
        direction: 'to-chargebee',
    },
    chargebee: {
        lane: 'chargebee',
        icon: 'file-invoice',
        tone: 'neutral',
        title: 'in Chargebee',
        station: 0,
        hint: 'Made in Chargebee. The connector has nothing of it yet: Chargebee still has to send the webhook.',
    },
    received: {
        lane: 'connector',
        icon: 'inbox',
        tone: 'waiting',
        title: 'webhook received',
        station: 1,
        hint: 'cbrcv has the webhook and is processing it.',
    },
    held: {
        lane: 'connector',
        icon: 'hourglass-half',
        tone: 'waiting',
        title: 'waiting for its customer in Exact',
        station: 1,
        hint: 'It can only be booked once its customer is in Exact. exsnd books the customer first; cbrcv tries again every 30 s.',
    },
    'in-connector': {
        lane: 'connector',
        icon: 'robot',
        tone: 'neutral',
        title: 'taken in, queued for exsnd',
        station: 2,
        hint: 'cbrcv took it in and queued it. exsnd still has to book it in Exact.',
    },
    retrying: {
        lane: 'connector',
        icon: 'rotate',
        tone: 'warn',
        title: 'booking failed, will try again',
        station: 2,
        hint: 'An attempt failed and the connector tries again. The Exact send queue only offers the message again after an hour, so a scenario rarely sees the next attempt.',
    },
    failed: {
        lane: 'connector',
        icon: 'circle-xmark',
        tone: 'error',
        title: 'booking failed, connector gave up',
        station: 2,
        hint: 'Every attempt failed. The connector does not try again by itself.',
    },
    booked: {
        lane: 'exact',
        icon: 'building-columns',
        tone: 'ok',
        title: 'booked in Exact',
        station: 3,
        hint: "The connector's relation names the Exact entry.",
    },
    handled: {
        lane: 'exact',
        icon: 'circle-check',
        tone: 'ok',
        title: 'handled, nothing to book',
        station: 3,
        hint: 'The connector is done with it without an Exact entry, for instance one it skips on purpose.',
    },
    elsewhere: {
        lane: 'exact',
        icon: 'circle-exclamation',
        tone: 'warn',
        title: 'in Exact, booked by another connector',
        station: 3,
        hint: "Exact has it while this connector's tables never heard of it: another connector, such as the deployed apps, did the work.",
    },
};

export const stageLook = (stage?: string) => STAGE_LOOK[stage ?? ''] ?? STAGE_LOOK.chargebee;

/** The stations a document passes, left to right; a stage is drawn at one of them. */
export const STATIONS = ['Chargebee', 'Webhook in', 'Connector', 'Exact'];

/** The stations of a payment made in Exact, which goes the other way. */
export const STATIONS_TO_CHARGEBEE = ['Exact', 'Webhook in', 'Connector', 'Chargebee'];

/** The stations a document at this stage passes, in the order it passes them. */
export const stationsOf = (stage?: string) =>
    stageLook(stage).direction === 'to-chargebee' ? STATIONS_TO_CHARGEBEE : STATIONS;

/** Whether the connector is done with a document, one way or the other. */
export const documentIsDone = (document: ScenarioRunDocument) =>
    ['booked', 'handled', 'elsewhere', 'failed', 'in-chargebee'].includes(document.stage ?? '');

/*
    A document's way through the connector, which the flow draws in full: the app that takes the webhook in, the app
    that writes to the other system, and where the document has to end up. From the connector's architecture, not from
    something the apps report: each table or queue the runner reads a stage from has one app working on it. A stage the
    runner did not see on its own - the customer that went from Chargebee straight to booked between two reads - still
    passed both apps, so both are drawn, as done.
*/
const ROUTE: Record<Direction, { apps: [string, string]; target: Lane; system: string }> = {
    'to-exact': { apps: ['cbrcv', 'exsnd'], target: 'exact', system: 'Exact' },
    'to-chargebee': { apps: ['exrcv', 'cbsnd'], target: 'chargebee', system: 'Chargebee' },
};

// What each part of the way does, per state, as a label on its card.
const APP_TEXT: Record<string, Partial<Record<WayState, string>>> = {
    cbrcv: {
        expected: 'Waiting for Chargebee webhook',
        busy: 'Processing webhook',
        waiting: 'Waiting for customer in Exact',
        done: 'Queued for exsnd',
        bypassed: 'Done by other connector',
    },
    exsnd: {
        expected: 'Waiting for cbrcv',
        busy: 'Booking in Exact',
        retrying: 'Failed, retrying',
        failed: 'Failed, gave up',
        done: 'Booked',
        bypassed: 'Done by other connector',
    },
    exrcv: {
        expected: 'Waiting for Exact webhook',
        busy: 'Processing webhook',
        done: 'Queued for cbsnd',
    },
    cbsnd: {
        expected: 'Waiting for exrcv',
        busy: 'Recording in Chargebee',
        done: 'Recorded',
    },
};

// The stage the document stands at says, for each of the two apps and the target, where it is.
const STAGE_WAY: Record<string, [WayState, WayState, WayState]> = {
    chargebee: ['expected', 'expected', 'expected'],
    received: ['busy', 'expected', 'expected'],
    held: ['waiting', 'expected', 'expected'],
    'in-connector': ['done', 'busy', 'expected'],
    retrying: ['done', 'retrying', 'expected'],
    failed: ['done', 'failed', 'expected'],
    booked: ['done', 'done', 'there'],
    handled: ['done', 'done', 'none'],
    elsewhere: ['bypassed', 'bypassed', 'there'],
    'paid-in-exact': ['expected', 'expected', 'expected'],
    'exact-webhook': ['busy', 'expected', 'expected'],
    'to-chargebee': ['done', 'busy', 'expected'],
    'in-chargebee': ['done', 'done', 'there'],
};

// Where a document starts: drawn as the step that made it, so not again as a part of its way.
const STARTING_STAGES = ['chargebee', 'paid-in-exact'];

const time = (at?: string) => (at ? new Date(at).getTime() : 0);

// What the runner says of a message the connector put off (from its messagewaits row): "postponed, next attempt at
// 00:20:07 UTC: <why>", "waiting for a parent, next attempt at …", and once that moment has passed
// "postponed until 00:20:07 UTC, back on the queue: <why>".
const PUT_OFF = /^(?:postponed|waiting for a parent), next attempt at (\d\d:\d\d:\d\d) UTC(?:, after attempt [^:]*)?(?:: (.*))?$/s;
const BACK_ON_QUEUE = /^[a-z ]+ until \d\d:\d\d:\d\d UTC, back on the queue(?:: (.*))?$/s;

/** Why the connector put a message off, in a few words; the whole sentence stays in the card's title. */
export const shortReason = (why?: string) => {
    const limit = /^Exact rate limit reached \(429\): the (daily|minutely) limit/.exec(why ?? '');
    const parent = /^Waiting for \w+ (\S+) to be in Exact/.exec(why ?? '');

    return limit
        ? `Exact's ${limit[1] === 'daily' ? 'day' : 'minute'} limit`
        : parent
          ? `waits for ${parent[1]}`
          : why;
};

/**
 * A UTC clock time the runner wrote, as the moment it is: on the day of {@link reference}, or the next day when
 * that would put it more than an hour before it.
 */
export const momentOf = (clock: string, reference: number): number => {
    const day = new Date(reference);
    const [hours, minutes, seconds] = clock.split(':').map(Number);
    const moment = Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), hours, minutes, seconds);

    return moment < reference - 3_600_000 ? moment + 86_400_000 : moment;
};

/**
 * How the card of an app shows a message the connector put off: waiting until a moment, or back on the queue once
 * it has passed. Undefined for anything else.
 */
const putOffLook = (
    detail: string | undefined,
    reference: number
): { state: WayState; text: string; detail?: string } | undefined => {
    const postponed = PUT_OFF.exec(detail ?? '');

    if (postponed) {
        return {
            state: 'waiting',
            text: `Postponed till ${new Date(momentOf(postponed[1], reference)).toLocaleTimeString()}`,
            detail: shortReason(postponed[2]),
        };
    }

    const back = BACK_ON_QUEUE.exec(detail ?? '');

    return back ? { state: 'busy', text: 'Back on the queue', detail: shortReason(back[1]) } : undefined;
};

/** The parts of a document's way, in the order it passes them, each with where it stands. */
export const documentWay = (document: ScenarioRunDocument, running: boolean): Extract<FlowRow, { kind: 'way' }>[] => {
    const id = document.id ?? '';
    const name = document.name ?? id;
    const amount = documentAmount(document);
    const direction = stageLook(document.stage).direction ?? 'to-exact';
    const route = ROUTE[direction];
    const states = STAGE_WAY[document.stage ?? ''] ?? STAGE_WAY.chargebee;
    const history = (document.history ?? []).filter((change) => !STARTING_STAGES.includes(change.stage ?? ''));

    // When the document first reached a station, or passed it: a stage seen later stands for the ones before it.
    const reached = (station: number) => history.find((change) => stageLook(change.stage).station >= station);
    const details = (stations: number[]) =>
        history
            .filter((change) => stations.includes(stageLook(change.stage).station) && change.detail)
            .map((change) => change.detail)
            .pop();

    const part = (
        index: 0 | 1 | 2,
        lane: Lane,
        app: string | undefined,
        text: string,
        at: string | undefined,
        doneAt: string | undefined,
        detail: string | undefined
    ): Extract<FlowRow, { kind: 'way' }> => {
        // An app whose message the connector put off waits rather than works, until the runner sees it back on the
        // queue: the card says so instead of "Booking in Exact" with a spinner.
        const putOff = running && app && states[index] === 'busy' ? putOffLook(detail, time(at) || Date.now()) : undefined;
        const state = putOff?.state ?? states[index];
        // Only a stretch the runner saw both ends of: a stage it missed between two reads took no time of its own.
        const took = state === 'done' && at && doneAt ? time(doneAt) - time(at) : undefined;

        return {
            kind: 'way',
            at: state === 'expected' ? '' : (at ?? ''),
            lane,
            part: app ? 'app' : 'target',
            app,
            name,
            id,
            amount,
            state,
            text: putOff?.text ?? text,
            tookMs: took && took > 0 ? took : undefined,
            sinceUtc: state === 'busy' || state === 'waiting' ? document.sinceUtc : undefined,
            detail: putOff ? putOff.detail : detail,
            fullDetail: putOff ? detail : undefined,
            link: { document: id, expected: state === 'expected' },
        };
    };

    const appText = (app: string, state: WayState) => {
        if (state === 'busy' && !running) {
            return 'Still busy when the run ended';
        }

        if (app === 'exsnd' && state === 'done' && document.stage === 'handled') {
            return 'Nothing to book';
        }

        // Once through, the wait for the customer is still worth a word: it is where the time went.
        if (app === 'cbrcv' && state === 'done' && history.some((change) => change.stage === 'held')) {
            return 'Queued, waited for customer';
        }

        return APP_TEXT[app]?.[state] ?? '';
    };

    const targetText = (() => {
        switch (states[2]) {
            case 'there':
                return document.stage === 'elsewhere'
                    ? 'Booked by other connector'
                    : direction === 'to-exact'
                    ? 'Booked in Exact'
                    : 'Recorded in Chargebee';
            case 'none':
                return 'Nothing booked';
            default:
                return `Not in ${route.system} yet`;
        }
    })();

    const firstApp = reached(1);
    const secondApp = reached(2);
    const arrived = reached(3);

    return [
        part(0, 'connector', route.apps[0], appText(route.apps[0], states[0]), firstApp?.atUtc, secondApp?.atUtc, details([1])),
        part(1, 'connector', route.apps[1], appText(route.apps[1], states[1]), secondApp?.atUtc, arrived?.atUtc, details([2])),
        part(2, route.target, undefined, targetText, arrived?.atUtc, undefined, details([3])),
    ];
};

/**
 * What a document is for, "€ 121,00": an invoice's total, what a payment paid. Undefined when the run did not say. A
 * payment made in Exact has no currency of its own: Exact books it in the division's, which is the euro.
 */
export const documentAmount = (document: ScenarioRunDocument): string | undefined => {
    if (document.amountInMinorUnits === undefined || document.amountInMinorUnits === null) {
        return undefined;
    }

    const amount = document.amountInMinorUnits / 100;
    const currency = document.currency || 'EUR';

    try {
        return new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(amount);
    } catch {
        // A code Intl does not know still reads as an amount.
        return `${amount.toFixed(2)} ${currency}`;
    }
};

// Lines the flow already says in another way: the phases, the documents' ways and the checks.
const SAID_ELSEWHERE = [
    /^started under profile/,
    /^waiting on /,
    /^waiting for the connector to finish/,
    /^letting the arrange settle/,
    /^reading the result back/,
    /^\d+ of \d+ checks failed/,
    /^passed: all/,
    /^nothing judged/,
    /^simulated wait/,
    /^customer .* is in Exact since/,
    /^the documents are done; still on its way/,
    // Nothing queued says nothing; a number above zero is worth the line.
    /^0 message\(s\) wait in the Exact send queue/,
    // The document's own "attempt failed" card carries the reason, at the same moment.
    /^\S+: an attempt failed \(/,
];

/**
 * What the now-line says the scenario does. While it waits on documents their cards say on what: the runner's
 * "waiting on …: Retrying after …: <reason>" would say it all again.
 */
export const nowTitle = (step: string | undefined, waitingOnDocuments: boolean) => {
    if (waitingOnDocuments && step?.startsWith('waiting on ')) {
        return 'waiting for the connector';
    }

    if (step?.startsWith(STILL_ON_ITS_WAY)) {
        const events = eventsOnTheirWay(step);

        return events > 0
            ? `waiting for ${events} Chargebee event${events === 1 ? '' : 's'} to reach the connector`
            : 'waiting for Chargebee events to reach the connector';
    }

    return step ?? '';
};

// The runner's line once the documents are done but webhooks are still on their way: every event with its id and
// what Chargebee said of it, three at most, then "and 5 more".
const STILL_ON_ITS_WAY = 'the documents are done; still on its way into the connector:';

/** How many events the runner's "still on its way" line names, counting the ones it summed up as "and N more". */
const eventsOnTheirWay = (step: string) =>
    (step.match(/\bev_[A-Za-z0-9]+/g)?.length ?? 0) + Number(/ and (\d+) more$/.exec(step)?.[1] ?? 0);

// The reader's "…; asking again in 48 s (attempt 1 of 3)" when Exact's limit refused a call.
const ASKING_AGAIN = /asking again in (\d+) s\b/;

/**
 * The step as it stands at this moment: a wait the runner wrote as "asking again in 48 s" counts down from when it
 * wrote that line, instead of standing still at 48 s until the next line comes.
 */
export const stepAt = (step: string | undefined, events: ScenarioRunEvent[], now: number) => {
    const wait = step ? ASKING_AGAIN.exec(step) : null;
    const said = wait ? [...events].reverse().find((event) => event.message === step)?.atUtc : undefined;

    if (!step || !wait || !said) {
        return step;
    }

    const left = new Date(said).getTime() + Number(wait[1]) * 1000 - now;

    return step.replace(ASKING_AGAIN, left >= 1000 ? `asking again in ${formatDuration(left)}` : 'asking again now');
};

// The step kinds that make a document of each name, for a document no step line names by its id.
const MAKERS: Record<string, string[]> = {
    customer: ['createCustomer'],
    invoice: ['createCharge', 'awaitInvoice', 'createSubscription'],
    creditNote: ['createCreditNote'],
    payment: ['payByHandInExact', 'recordPayment', 'payInvoicesTogether'],
};

/**
 * The step that made a document: the one whose lines name it; otherwise the last step of the kind that makes such a
 * document, up to the moment it was there; otherwise the last step in its lane up to then. Not simply the last step
 * before it: a customer and an item price are often made in the same second.
 */
const makerOf = (
    document: ScenarioRunDocument,
    startedAt: string | undefined,
    steps: Extract<FlowRow, { kind: 'step' }>[]
): Extract<FlowRow, { kind: 'step' }> | undefined => {
    const id = document.id ?? '';
    const named = steps.find((step) => id && [step.title, ...step.notes].some((line) => line.includes(id)));

    if (named) {
        return named;
    }

    const upTo = steps.filter((step) => time(step.at) <= time(startedAt) + 1000);
    const ofKind = upTo.filter((step) => step.step && (MAKERS[document.name ?? ''] ?? []).includes(step.step)).pop();

    return ofKind ?? upTo.filter((step) => step.lane === stageLook(document.history?.[0]?.stage).lane).pop();
};

/**
 * Turns a scenario's state into the lines of its flow, oldest first: its stretches as headings, the steps in the lane
 * of the system they act on, each document's way - both connector apps and where it ends up - and what went wrong
 * across the lanes. What has not happened yet comes last, dimmed.
 *
 * The progress lines of a wait are left out: the documents' ways are what they said, with a time to each.
 *
 * @param running whether the scenario still runs: only then is an app at work right now.
 */
// The document a check names, which makes ten checks of the same kind ten different names.
const DOCUMENT_ID = /\s+(?:of|for|on|against)\s+\S+$|\s+against\s+\S+(?=\s+in\s)/;

/**
 * One line per kind of failed check: what was expected, what came back, and how often, without the document ids.
 * Ten checks that failed the same way read as one line with ×10, not as ten long names in a row.
 */
export const failedChecks = (checks: ScenarioRunCheck[]): string[] => {
    const lines = new Map<string, number>();

    for (const check of checks) {
        const what = (check.name ?? 'check').replace(DOCUMENT_ID, '');
        const outcome =
            check.expected !== undefined || check.actual !== undefined
                ? `: expected ${check.expected ?? '—'}, got ${check.actual ?? '—'}`
                : '';
        const line = `${what}${outcome}`;

        lines.set(line, (lines.get(line) ?? 0) + 1);
    }

    return [...lines].map(([line, count]) => (count > 1 ? `${line} ×${count}` : line));
};

export const scenarioFlow = (
    scenario: ScenarioRunScenario,
    events: ScenarioRunEvent[],
    running = false
): FlowRow[] => {
    const { phases } = scenarioPhases(scenario, events);
    const rows: FlowRow[] = phases
        .filter(phaseSaysSomething)
        .map((phase) => ({ kind: 'phase', at: phase.startedAtUtc, phase }));

    // The step whose own lines are still coming: what it made, where it posted. Any other line ends it.
    let openStep: Extract<FlowRow, { kind: 'step' }> | undefined;
    const steps: Extract<FlowRow, { kind: 'step' }>[] = [];

    for (const event of events) {
        const at = event.atUtc ?? '';
        const text = event.message ?? '';

        if (event.step) {
            const look = STEP_LOOK[event.step] ?? { lane: 'connector' as Lane, icon: 'gear' as IconProp };

            openStep = {
                kind: 'step',
                at,
                lane: look.lane,
                icon: look.icon,
                title: text,
                notes: [],
                app: look.app,
                links: [],
                step: event.step,
            };
            steps.push(openStep);
            rows.push(openStep);
            continue;
        }

        if (event.level === 'warning' || event.level === 'error') {
            openStep = undefined;
            rows.push({ kind: 'problem', at, level: event.level, text });
            continue;
        }

        if (SAID_ELSEWHERE.some((pattern) => pattern.test(text))) {
            openStep = undefined;
            continue;
        }

        if (openStep) {
            openStep.notes.push(text);
            continue;
        }

        // Not a step's own line, so something the run saw while it waited: the connector's side.
        rows.push({ kind: 'note', at, lane: 'connector', text });
    }

    for (const document of scenario.documents ?? []) {
        const start = (document.history ?? []).find((change) => STARTING_STAGES.includes(change.stage ?? ''));

        if (start) {
            makerOf(document, start.atUtc, steps)?.links.push({ document: document.id ?? '', expected: false });
        }

        rows.push(...documentWay(document, running));
    }

    const checks = scenario.checks ?? [];

    if (checks.length > 0) {
        const judged = checks.filter((check) => checkKind(check.status) !== 'skipped');

        rows.push({
            kind: 'checks',
            at: scenario.finishedAtUtc ?? phases[phases.length - 1]?.startedAtUtc ?? '',
            passed: judged.filter((check) => checkKind(check.status) === 'passed').length,
            total: judged.length,
            failed: failedChecks(judged.filter((check) => checkKind(check.status) === 'failed')),
        });
    }

    // A heading goes before what happened at the same moment, what has not happened yet after everything that did;
    // otherwise the order they were written in stands, which keeps a document's parts in the order it passes them.
    const order = (row: FlowRow) => (row.kind === 'phase' ? 0 : row.kind === 'checks' ? 2 : 1);
    const toCome = (row: FlowRow) => (row.kind === 'way' && !row.at ? 1 : 0);

    const sorted = rows
        .map((row, index) => ({ row, index }))
        .sort(
            (a, b) =>
                toCome(a.row) - toCome(b.row) ||
                time(a.row.at) - time(b.row.at) ||
                order(a.row) - order(b.row) ||
                a.index - b.index
        )
        .map(({ row }) => row);

    // While the scenario runs, its last step is what it is doing, until a phase after it begins.
    const lastStep = sorted.map((row) => row.kind).lastIndexOf('step');

    if (running && lastStep >= 0 && !sorted.slice(lastStep + 1).some((row) => row.kind === 'phase')) {
        (sorted[lastStep] as Extract<FlowRow, { kind: 'step' }>).going = true;
    }

    return sorted;
};
